import type {MarkdownEditHistoryViewer_comment$key} from '@github-ui/markdown-edit-history-viewer'
import {MarkdownEditHistoryViewer} from '@github-ui/markdown-edit-history-viewer'
import {Box, Label, RelativeTime, Text} from '@primer/react'

import type {IssueBodyHeaderAuthor$key} from './__generated__/IssueBodyHeaderAuthor.graphql'
import {IssueBodyAuthorAvatar} from './IssueBodyAuthorAvatar'
import {IssueBodyHeaderActions, type IssueBodyHeaderActionsProps} from './IssueBodyHeaderActions'
import {IssueBodyHeaderAuthor} from './IssueBodyHeaderAuthor'

export type IssueBodyHeaderProps = {
  additionalActions?: React.ReactNode
  editHistory: MarkdownEditHistoryViewer_comment$key
  createdAt: string
  isPullRequest?: boolean
  actionProps?: IssueBodyHeaderActionsProps
  author: IssueBodyHeaderAuthor$key | null
  viewerDidAuthor: boolean
}
export function IssueBodyHeader({
  additionalActions,
  createdAt,
  editHistory,
  isPullRequest,
  author,
  actionProps,
  viewerDidAuthor,
}: IssueBodyHeaderProps) {
  return (
    <Box
      sx={{
        backgroundColor: viewerDidAuthor ? 'accent.subtle' : 'canvas.subtle',
        borderTopLeftRadius: 2,
        borderTopRightRadius: 2,
        borderBottom: '1px solid',
        borderBottomColor: viewerDidAuthor ? 'accent.muted' : 'border.muted',
        color: 'fg.muted',
        display: 'flex',
        flexDirection: 'row',
        flex: 1,
        fontSize: 1,
        gap: 0,
        py: 2,
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          minHeight: 'var(--control-small-size, 28px)',
          display: 'flex',
          justifyContent: 'space-between',
          flexGrow: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            px: 3,
            flexGrow: 1,
            flexShrink: 1,
            flexDirection: 'row',
            gap: 2,
            alignItems: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <IssueBodyAuthorAvatar author={author} />
          <Box
            as="h3"
            sx={{
              display: 'flex',
              flexDirection: ['column', 'column', 'row', 'row'],
              flexWrap: 'wrap',
              columnGap: 1,
              overflow: 'hidden',
              fontSize: 'unset',
              fontWeight: 'unset',
              margin: 'unset',
            }}
          >
            {author && <IssueBodyHeaderAuthor showLogin author={author} />}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                fontSize: [0, 1, 1, 1],
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <Text sx={{fontSize: 'inherit', whiteSpace: 'nowrap', display: ['none', 'inline', 'inline', 'inline']}}>
                {isPullRequest ? 'created' : 'opened this issue'}
              </Text>
              <RelativeTime sx={{fontSize: 'inherit'}} date={new Date(createdAt)} />
              {/** As Pull Request Issue Body Descriptions show Author label to the right of created at date, we need to render this additional label */}
              {isPullRequest && <Label sx={{ml: 2}}>Author</Label>}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            gap: 1,
            pl: [0, 3, 3],
            pr: 2,
            display: 'flex',
            flexGrow: 1,
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <MarkdownEditHistoryViewer editHistory={editHistory} />
          {additionalActions}
          {actionProps && <IssueBodyHeaderActions {...actionProps} />}
        </Box>
      </Box>
    </Box>
  )
}

try{ IssueBodyHeader.displayName ||= 'IssueBodyHeader' } catch {}